import type { PresetListing } from "@pax/api-contract/presets/v1alpha1";
import { focusHaloFlush, HapticSurface, panelMotion, press } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { type ReactNode, useState } from "react";
import { compactCount, listedAs } from "../facts";
import { LandingArtwork } from "./LandingArtwork";

/**
 * How an arriving picture replaces the one before it: long, and on the panel
 * system's curve, so most of the change is over early and the last of it is
 * barely a change. A photograph that arrives at a constant speed reads as a
 * slideshow.
 */
const DISSOLVE =
  "transition-opacity duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none";

/**
 * The scenario the store is arguing for, as the thing everything else is on
 * top of rather than as the first item on a page.
 *
 * It is pinned and takes exactly its own height out of the flow, so the shelf
 * that follows it rises over it as the page is scrolled and uncovers it again
 * on the way back. Nothing is intercepted to do that: it is two elements in
 * one scroller.
 *
 * Nothing here keeps a timer. The bar under the dots *is* the wait — it fills
 * over one scenario's turn and the store moves on when it is full — so the
 * pointer holding the rotation is one paused animation rather than a clock
 * that has to be stopped and a bar that has to be told about it. The lengths
 * are `.store-featured` in the stylesheet, beside the drift they also govern.
 */
export function FeaturedScenario({
  listings,
  controls,
}: {
  listings: PresetListing[];
  /** The store's own two ways out, which ride on the artwork. */
  controls: ReactNode;
}) {
  const [current, setCurrent] = useState(0);
  const [held, setHeld] = useState(false);

  const listing = listings[current];
  if (!listing) return null;
  const go = (position: number) => setCurrent((position + listings.length) % listings.length);
  const holding = { animationPlayState: held ? "paused" : "running" } as const;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover only holds the rotation; every slide is reachable from the dots
    <div
      // The artwork carries the sheet's own corners rather than borrowing
      // them from the scroller that clips it. At rest the two sit exactly on
      // top of each other; the difference is an overscroll, where the picture
      // pulls away from the clip and would otherwise square off as it goes.
      className="store-featured group/featured sticky top-0 h-(--landing-hero) app-screen-top-corners overflow-hidden"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
    >
      {/* Only the picture with the screen moves, and it moves the whole time
          it has it. Every other landing image already sits at the size that drift
          ends on, so the one being replaced holds what it pushed in to while
          it fades and the handover is a dissolve and nothing else. */}
      {listings.map((entry, position) => (
        <div
          key={entry.slug}
          aria-hidden={position !== current}
          style={holding}
          className={`absolute inset-0 size-full scale-(--store-drift) ${DISSOLVE} ${
            position === current ? "store-drift opacity-100" : "opacity-0"
          }`}
        >
          <LandingArtwork
            landing={entry.landing}
            fallback={entry.cover}
            play={position === current}
            priority={position === 0}
            className="size-full"
          />
        </div>
      ))}

      {/* One scrim, weighted at both ends, because the artwork carries the
          store's chrome at the top and the scenario's name at the foot. A
          photograph somebody else chose cannot promise either on its own. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(to_bottom,rgb(0_0_0/0.45)_0%,transparent_26%,transparent_44%,rgb(0_0_0/0.75)_100%)]"
      />

      <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-4 p-4 sm:p-5">
        {controls}
      </div>

      <div
        key={listing.slug}
        className="absolute inset-x-0 bottom-0 flex animate-in items-end justify-between gap-6 p-6 duration-700 fade-in slide-in-from-bottom-3 sm:p-8"
      >
        <div className="min-w-0">
          <p className="text-3xl font-semibold tracking-tight text-balance text-white sm:text-5xl">
            {listing.title}
          </p>
          <p className="mt-2 text-sm text-white/70">
            {listedAs(listing)} &middot; {compactCount(listing.gamesStarted)} games
          </p>
          <Link
            to="/presets/$slug"
            params={{ slug: listing.slug }}
            className={`relative mt-5 flex h-10 w-fit items-center gap-2 rounded-full bg-white px-5 text-sm font-medium text-black hover:bg-white/90 ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            <Play className="size-3.5 fill-current" />
            Open
            <HapticSurface />
          </Link>
        </div>

        {/* Which scenario has the screen, and how much of its turn is left.
            The one in play is a track with the turn drawn across it; the rest
            are the same mark at the width of a dot. */}
        {listings.length > 1 && (
          <div className="flex shrink-0 items-center gap-1.5 pb-1.5">
            {listings.map((entry, position) => (
              <button
                key={entry.slug}
                type="button"
                aria-label={`Show ${entry.title}`}
                // Only when true: `aria-current={false}` serializes to the
                // string "false", which assistive tech reads as current.
                {...(position === current && { "aria-current": true as const })}
                onClick={() => go(position)}
                className={`h-1.5 rounded-full bg-white/40 ${panelMotion} ${
                  position === current ? "w-5" : "w-1.5 hover:bg-white/70"
                }`}
              >
                {position === current && (
                  <span
                    className="store-dwell block size-full rounded-full bg-white"
                    style={holding}
                    onAnimationEnd={() => go(current + 1)}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* As quiet as the dots: a glyph and nothing behind it. The loudest thing
          on this page is supposed to be a photograph. */}
      {listings.length > 1 &&
        (
          [
            [-1, "left-3 sm:left-4", "Previous scenario"],
            [1, "right-3 sm:right-4", "Next scenario"],
          ] as const
        ).map(([direction, side, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => go(current + direction)}
            aria-label={label}
            className={`absolute top-1/2 ${side} -translate-y-1/2 text-white/60 opacity-0 transition-opacity duration-200 group-hover/featured:opacity-100 hover:text-white focus-visible:opacity-100 ${focusHaloFlush}`}
          >
            {direction < 0 ? (
              <ChevronLeft className="size-6" strokeWidth={1.75} />
            ) : (
              <ChevronRight className="size-6" strokeWidth={1.75} />
            )}
          </button>
        ))}
    </div>
  );
}
